import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { OrganizationPortalNavigation } from "@/components/portal/OrganizationPortalNavigation";
import { PlatformPortalNavigation } from "@/components/platform/PlatformPortalNavigation";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { MaintenanceBanner } from "@/components/maintenance/MaintenanceBanner";
import { AssistantPanel } from "@/components/assistant/AssistantPanel";
import { ForcePasswordChangeGate } from "@/components/auth/ForcePasswordChangeGate";
import { useAuth } from "@/hooks/useAuth";
import { usesClientPortalShell, usesPlatformPortalShell } from "@/lib/portalShell";
import "@/styles/platform-scope.css";

const rolePrefixes: Record<string, string> = {
  PLATFORM_OWNER: "(po)",
  PLATFORM_ADMIN: "(pa)",
  ORG_OWNER: "(o)",
  ORG_ADMIN: "(a)",
};

export function AppShell() {
  const { role, orgUser, isImpersonating } = useAuth();
  const { pathname } = useLocation();
  const isClientPortal = usesClientPortalShell(role, pathname);
  const isPlatformPortal = usesPlatformPortalShell(role, pathname);
  const isPortalShell = isClientPortal || isPlatformPortal;

  useEffect(() => {
    if (!role) return;
    const prefix = rolePrefixes[role] ?? "";
    const productName = isPortalShell ? "Client Portal" : "Studio Manager";
    document.title = prefix ? `${prefix} ${productName}` : productName;
  }, [isPortalShell, role]);

  return (
    <div className={isPortalShell ? "flex h-screen flex-col bg-[#f7f3eb] lg:flex-row" : "flex h-screen bg-white"}>
      {isClientPortal && role ? (
        <OrganizationPortalNavigation
          orgId={orgUser?.orgId ?? undefined}
          role={role}
          isImpersonating={isImpersonating}
          userName={orgUser?.name}
          userEmail={orgUser?.email}
        />
      ) : isPlatformPortal && role ? (
        <PlatformPortalNavigation role={role} userName={orgUser?.name} userEmail={orgUser?.email} />
      ) : (
        <Sidebar />
      )}
      <div className={isPlatformPortal ? "platform-scope flex flex-1 flex-col overflow-hidden" : "flex flex-1 flex-col overflow-hidden"}>
        <ImpersonationBanner />
        <MaintenanceBanner />
        <main
          className={
            isPortalShell
              ? "flex-1 overflow-auto bg-[#f7f3eb] px-4 py-6 sm:px-6 lg:px-8 lg:py-9"
              : "flex-1 overflow-auto bg-white p-6"
          }
        >
          <ForcePasswordChangeGate>
            <Outlet />
          </ForcePasswordChangeGate>
        </main>
      </div>
      <AssistantPanel />
    </div>
  );
}
